import { getQueryKey } from '@trpc/react-query'
import { queryClient } from '../lib/query-client'
import { useMessagesStore } from '../stores/messages.store'
import { useTerminalStore } from '../stores/terminal.store'
import { trpc } from '../lib/trpc'
import { dbg, dbgEvent } from '../lib/debug'

// Type local — correspond au type retourné par messages.list
interface MessageRow {
  id: string
  session_id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  content_type: 'text' | 'audio_transcript' | 'file_ref'
  status: 'sending' | 'streaming' | 'complete' | 'error'
  metadata: Record<string, unknown> | null
  created_at: Date
}

export const messagesHandler = {
  onPartial(sessionId: string, data: { content: string }): void {
    dbg('Handler', `messages.onPartial sid=${sessionId.slice(0, 8)}... chunk=${data.content.length}`)
    useMessagesStore.getState().appendStreamChunk(sessionId, data.content)
  },

  onComplete(sessionId: string, data: { messageId: string; content: string }): void {
    dbg('Handler', `messages.onComplete sid=${sessionId.slice(0, 8)}...`)
    dbgEvent('RQ', 'setQueryData message:complete', { sessionId: sessionId.slice(0, 8), messageId: data.messageId })

    const assistantMessage = {
      id: data.messageId,
      session_id: sessionId,
      role: 'assistant' as const,
      content: data.content,
      content_type: 'text' as const,
      status: 'complete' as const,
      metadata: null,
      created_at: new Date(),
    }

    // Injecter dans React Query. `old` est undefined dans deux cas : (1) la query n'a jamais
    // encore été chargée (conversation neuve — cb87ab16), (2) le cache a été ÉVINCÉ après ≥5 min
    // sans observateur (onglet Pulsar en arrière-plan — debug-historique-perdu-retour-onglet-
    // 2026-08-12 §3). Dans CE cas (jamais sur le chemin peuplé ci-dessous), on marque la query
    // invalidée pour qu'elle se recharge en entier au PROCHAIN montage, malgré staleTime:Infinity.
    //
    // refetchType:'none' est volontaire, pas paresseux : on ne déclenche JAMAIS de refetch ici,
    // seulement au prochain montage. Un refetch immédiat courrait contre les prochaines écritures
    // WebSocket sur CE MÊME cache et pourrait écraser un état plus frais avec une réponse REST
    // plus ancienne — exactement la race #2768 (avril 2026, raciné deux fois la même nuit à deux
    // endroits différents : doc §10.1). Ne pas retirer refetchType:'none' sans relire ce paragraphe.
    //
    // Fenêtre résiduelle assumée (pas fermée à 100%, volontairement) : au prochain montage, la
    // query invalidée déclenche un rechargement depuis la base. Si un event temps réel arrive
    // PENDANT que ce rechargement est en vol, sa réponse — un instantané légèrement antérieur —
    // peut l'écraser. Fenêtre brève, ne survient que dans un cas déjà dégradé (cache tronqué), et
    // sans perte réelle : le message reste en base, il réapparaît au montage suivant. À comparer
    // au mal actuel : un historique tronqué certain et permanent jusqu'à rechargement manuel.
    const key = getQueryKey(trpc.messages.list, { sessionId, limit: 50 }, 'query')
    let cacheWasMissing = false
    queryClient.setQueryData(key, (old: MessageRow[] | undefined) => {
      if (!old) { cacheWasMissing = true; return [assistantMessage] }
      if (old.some(m => m.id === data.messageId)) return old
      return [...old, assistantMessage]
    })
    if (cacheWasMissing) {
      void queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' })
    }

    // Reset streaming dans messages.store
    useMessagesStore.getState().resetStreaming(sessionId)

    // Invalider terminal events historiques
    const terminalKey = getQueryKey(trpc.messages.terminalEvents.listBySession, { sessionId }, 'query')
    void queryClient.invalidateQueries({ queryKey: terminalKey })

    // Invalider contextUsage (remplace le listener bus dans StatusBar — Phase B2)
    const contextKey = getQueryKey(trpc.sessions.contextUsage, { sessionId }, 'query')
    void queryClient.invalidateQueries({ queryKey: contextKey })
  },

  onError(sessionId: string, data: { error: string; partialContent?: string }): void {
    dbg('Handler', `messages.onError sid=${sessionId.slice(0, 8)}...`)
    const store = useMessagesStore.getState()
    const session = store.sessions[sessionId]
    const contentToPreserve = data.partialContent ?? session?.streamingContent

    // TERM (client-only, journal-minidev-tout-afficher-terminal-pulsar-2026-08-11) : capture le
    // texte d'erreur INCONDITIONNELLEMENT, y compris quand contentToPreserve est vide (ex. « Session
    // not found », tour mort avant tout texte streamé — jusqu'ici jeté au sol, jamais visible nulle
    // part côté client). Ajout PUR dans terminal.store.ts (jamais lu par DISC) : ne change rien au
    // comportement ci-dessous vis-à-vis de trpc.messages.list / MessageList.tsx.
    useTerminalStore.getState().appendClientError(sessionId, data.error)

    if (contentToPreserve && contentToPreserve.length > 0) {
      const errorMessageId = `error-${Date.now()}`
      const errorMessage = {
        id: errorMessageId,
        session_id: sessionId,
        role: 'assistant' as const,
        content: contentToPreserve,
        content_type: 'text' as const,
        status: 'error' as const,
        metadata: null,
        created_at: new Date(),
      }
      // Même garde qu'onComplete ci-dessus (commentaire détaillé) : refetchType:'none' UNIQUEMENT
      // sur le chemin !old (cache vide/évincé), jamais sur le chemin peuplé — ne rouvre pas #2768.
      const key = getQueryKey(trpc.messages.list, { sessionId, limit: 50 }, 'query')
      let cacheWasMissing = false
      queryClient.setQueryData(key, (old: MessageRow[] | undefined) => {
        if (!old) { cacheWasMissing = true; return [errorMessage] }
        return [...old, errorMessage]
      })
      if (cacheWasMissing) {
        void queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' })
      }
    }

    store.resetStreaming(sessionId)
  },
}
